import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { getUserEntity } from 'common/UserEntityProvider';
import InvoicesSearchBar from './InvoicesSearchBar';
import InvoicesStatusSelect from './InvoicesStatusSelect';
import InvoicesAmountRange from './InvoicesAmountRange';
import DateRangePickerDialog from 'components/Dialog/DateRangePickerDialog';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import SearchButton from 'components/common/SearchButton';
import AddIcon from "@mui/icons-material/Add";
import { ENTITY_TYPE } from 'common/constants';
import intl from 'components/i18n/ReactIntlWrapper';

const InvoicesNavbar = ({ 
    onSearch, 
    invoiceStatus, 
    allInvoices, 
    showPlusButton = false, 
    queryString, 
    setQueryString,
    initialIdStatus = null,
    initialRange = null,
    initialAmounts = { min: "", max: "" }
}) => {
    const navigate = useNavigate();
    const searchBarRef = useRef(null);
    const [amounts, setAmounts] = useState(initialAmounts);
    const [idStatus, setIdStatus] = useState(initialIdStatus);
    const [range, setRange] = useState(initialRange);

    useEffect(() => {
        setAmounts(prev => {
            const normalized =
                initialAmounts && typeof initialAmounts === 'object'
                    ? { min: initialAmounts.min || "", max: initialAmounts.max || "" }
                    : { min: "", max: "" };
            if (
                prev.min === normalized.min &&
                prev.max === normalized.max
            ) {
                return prev;
            }
            return normalized;
        });
    }, [initialAmounts.min, initialAmounts.max]);

    useEffect(() => {
        setIdStatus(initialIdStatus);
    }, [initialIdStatus]);

    useEffect(() => {
        setRange(initialRange);
    }, [initialRange]);

    // Validation helpers
    const isAmountRangeValid = useCallback(() => {
        const minValue = amounts?.min ? parseFloat(amounts.min) : null;
        const maxValue = amounts?.max ? parseFloat(amounts.max) : null;
        if (minValue !== null && maxValue !== null) {
            return maxValue >= minValue;
        }
        return true;
    }, [amounts]);

    const isDateRangeValid = useCallback(() => {
        if (!range?.from || !range?.to) return true;
        return new Date(range.from) <= new Date(range.to);
    }, [range]);

    const handleSearchButton = useCallback(() => {
        // Validate before searching
        if (!isAmountRangeValid()) {
            return; // Don't search if amount validation fails
        }
        if (!isDateRangeValid()) {
            return; // Don't search if date validation fails
        }
        
        const currentQueryString = searchBarRef.current?.getValue() || '';
        setQueryString(currentQueryString);
        onSearch(currentQueryString, idStatus, range, amounts);
    }, [idStatus, range, amounts, onSearch, setQueryString, isAmountRangeValid, isDateRangeValid]);

    const handleSearchButtonValidating = useCallback(() => {
        const currentQueryString = searchBarRef.current?.getValue() || '';
        setQueryString(currentQueryString);
        onSearch(currentQueryString, idStatus);
    }, [idStatus, onSearch, setQueryString]);

    // Memoize the onEnterPress handler to prevent creating new references
    const handleEnterPress = useCallback(() => {
        const currentQueryString = searchBarRef.current?.getValue() || '';
        setQueryString(currentQueryString);
        if (allInvoices) {
            // Validate before searching
            if (!isAmountRangeValid() || !isDateRangeValid()) {
                return; // Don't search if validation fails
            }
            onSearch(currentQueryString, idStatus, range, amounts);
        } else {
            onSearch(currentQueryString, idStatus);
        }
    }, [allInvoices, idStatus, range, amounts, onSearch, setQueryString, isAmountRangeValid, isDateRangeValid]);

    const handleStatusChange = useCallback((e) => {
        if (e !== null) {
            setIdStatus(e.target.value);
        } else {
            setIdStatus(null);
        }
    }, []);

      const entityType = getUserEntity().entityType;

    return (
        <Grid
          container
          spacing={2} sx={{ mb: 2 }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid sx={{xs:12, md:(allInvoices ? 2 : 10)}}>
            <InvoicesSearchBar
              ref={searchBarRef}
              onEnterPress={handleEnterPress}
              queryString={queryString}
            />
          </Grid>

          {allInvoices && (
            <Grid sx={{xs:12, md:3.5}}>
              <DateRangePickerDialog
                disabled={false}
                inputName="dateRange"
                className="timesheet-datepicker"
                selected={range}
                setRange={setRange}
                required
                gap={2}
              />
            </Grid>
          )}

          {allInvoices && (
            <Grid sx={{xs:12, md:2}}>
              <InvoicesStatusSelect
                invoiceStatus={invoiceStatus}
                selectedStatus={idStatus}
                handleStatusChange={handleStatusChange}
              />
            </Grid>
          )}

          {allInvoices && (
            <Grid sx={{xs:12, md:2.5}}>
              <InvoicesAmountRange
                amounts={amounts}
                setAmounts={setAmounts}
                gap={2}
              />
            </Grid>
          )}

          <Grid sx={{xs:12, md:1}}>
            <SearchButton
              clickEvent={allInvoices ? handleSearchButton : handleSearchButtonValidating}
            />
          </Grid>
      
          {showPlusButton && (
            <Grid sx={{display: 'flex', justifyContent: 'flex-end', xs:12, md:"auto"}}>
              <Button
                onClick={() =>
                  entityType === ENTITY_TYPE.CORPORATE_ENTITY
                    ? navigate(`/invoicing/newinvoice`)
                    : navigate(`/myInvoices/upload`)
                }
                sx={{ minWidth: 40, height: 40 }}
              >
                <AddIcon /> {intl.get('invoice.new') ? intl.get('invoice.new') : intl.get('button.new')}
              </Button>
            </Grid>
          )}
        </Grid>);
}

export default React.memo(InvoicesNavbar);
